/**
 * Device Detection Utility for Smart TVs
 * Detects LG WebOS, Samsung Tizen, and other Smart TV platforms
 * 
 * @module deviceDetection
 */

// Cache detection results
let cachedPlatform = null;
let cachedCapabilities = null;

/**
 * Platform types
 */
export const PLATFORMS = {
  LG_WEBOS: 'lg-webos',
  SAMSUNG_TIZEN: 'samsung-tizen',
  ANDROID_TV: 'android-tv',
  FIRE_TV: 'fire-tv',
  ROKU: 'roku',
  WEB_BROWSER: 'web-browser',
  UNKNOWN: 'unknown'
};

/**
 * Detect the current platform
 * @returns {string} Platform identifier
 */
export function detectPlatform() {
  if (cachedPlatform) return cachedPlatform;

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const uaLower = ua.toLowerCase();

  // LG WebOS detection
  if (uaLower.includes('webos') ||
    uaLower.includes('web0s') ||
    uaLower.includes('netcast') ||
    (typeof window !== 'undefined' && window.webOS)) {
    cachedPlatform = PLATFORMS.LG_WEBOS;
  }
  // Samsung Tizen detection
  else if (uaLower.includes('tizen') ||
    uaLower.includes('samsung') ||
    (typeof window !== 'undefined' && window.tizen)) {
    cachedPlatform = PLATFORMS.SAMSUNG_TIZEN;
  }
  // Android TV detection
  else if ((uaLower.includes('android') && uaLower.includes('tv')) ||
    (uaLower.includes('android') && uaLower.includes('large'))) {
    cachedPlatform = PLATFORMS.ANDROID_TV;
  }
  // Fire TV detection
  else if (uaLower.includes('aftb') ||
    uaLower.includes('afts') ||
    uaLower.includes('aftm') ||
    uaLower.includes('silk')) {
    cachedPlatform = PLATFORMS.FIRE_TV;
  }
  // Roku detection
  else if (uaLower.includes('roku') ||
    (typeof window !== 'undefined' && window.roku)) {
    cachedPlatform = PLATFORMS.ROKU;
  }
  // Standard web browser
  else if (typeof window !== 'undefined' && window.document) {
    cachedPlatform = PLATFORMS.WEB_BROWSER;
  }
  else {
    cachedPlatform = PLATFORMS.UNKNOWN;
  }

  return cachedPlatform;
}

/**
 * Get WebOS version (if applicable)
 * @returns {number|null} WebOS version number or null
 */
export function getWebOSVersion() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  // Pattern: "Web0S; Linux/SmartTV) Chrome/XX" or "WebOS/X.X"
  const webosMatch = ua.match(/WebOS\/(\d+)/i) ||
    ua.match(/web0s[^)]*Chrome\/(\d+)/i);

  if (webosMatch) {
    const chromeVersion = parseInt(webosMatch[1], 10);
    // Map Chrome version to approximate WebOS version
    // WebOS 3.x ~ Chrome 38-47
    // WebOS 4.x ~ Chrome 53-68
    // WebOS 5.x ~ Chrome 68-79
    // WebOS 6.x ~ Chrome 79+
    if (chromeVersion < 47) return 3;
    if (chromeVersion < 68) return 4;
    if (chromeVersion < 79) return 5;
    return 6;
  }

  return null;
}

/**
 * Get Tizen version (if applicable)
 * @returns {number|null} Tizen version number or null
 */
export function getTizenVersion() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  // Pattern: "Tizen X.X" or "TizenBrowser/X.X"
  const tizenMatch = ua.match(/Tizen[/\s]?(\d+)/i);

  if (tizenMatch) {
    return parseInt(tizenMatch[1], 10);
  }

  return null;
}

/**
 * Check if running on a legacy (older) Smart TV
 * @returns {boolean}
 */
export function isLegacyTV() {
  const platform = detectPlatform();

  if (platform === PLATFORMS.LG_WEBOS) {
    const version = getWebOSVersion();
    return version !== null && version <= 4;
  }

  if (platform === PLATFORMS.SAMSUNG_TIZEN) {
    const version = getTizenVersion();
    return version !== null && version <= 4;
  }

  return false;
}

/**
 * Check if the device is any Smart TV
 * @returns {boolean}
 */
export function isSmartTV() {
  const platform = detectPlatform();
  return [
    PLATFORMS.LG_WEBOS,
    PLATFORMS.SAMSUNG_TIZEN,
    PLATFORMS.ANDROID_TV,
    PLATFORMS.FIRE_TV,
    PLATFORMS.ROKU
  ].includes(platform);
}

/**
 * Detect device capabilities
 * @returns {Object} Capabilities object
 */
export function getDeviceCapabilities() {
  if (cachedCapabilities) return cachedCapabilities;

  const platform = detectPlatform();
  const isLegacy = isLegacyTV();

  // Default capabilities (modern browser)
  cachedCapabilities = {
    platform: platform,
    isSmartTV: isSmartTV(),
    isLegacy: isLegacy,

    // Video format support. HLS.js supplies HLS on legacy TV Chromium builds.
    video: {
      mp4: true,
      hls: true,
      dash: false,
      preferredFormat: 'hls',
    },

    // CSS feature support
    css: {
      flexGap: !isLegacy,
      cssVariables: !isLegacy,
      backdropFilter: !isLegacy && platform !== PLATFORMS.LG_WEBOS,
      aspectRatio: !isLegacy,
      clamp: !isLegacy,
      grid: !isLegacy,
    },

    // JavaScript feature support (may need polyfills)
    js: {
      fetch: typeof fetch !== 'undefined',
      promise: typeof Promise !== 'undefined',
      proxy: typeof Proxy !== 'undefined',
      symbol: typeof Symbol !== 'undefined',
      arrayFlat: Array.prototype.flat !== undefined,
    },

    // Hardware capabilities
    hardware: {
      maxVideoResolution: isLegacy ? '1080p' : '4k',
      webGL: hasWebGL(),
      touchscreen: false,
    }
  };

  return cachedCapabilities;
}

/**
 * Check WebGL support
 * @returns {boolean}
 */
function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

/**
 * Get recommended video source based on device
 * @param {Array} sources - Array of video sources with {src, type}
 * @returns {Object|null} Best video source for this device
 */
export function getBestVideoSource(sources) {
  if (!sources || !Array.isArray(sources) || sources.length === 0) {
    return null;
  }

  const hlsSource = sources.find(s =>
    (s.type && s.type.toLowerCase().includes('mpegurl')) ||
    (s.src && s.src.toLowerCase().includes('.m3u8'))
  );

  if (hlsSource) {
    return {
      ...hlsSource,
      type: 'application/x-mpegURL'
    };
  }

  // Temporary compatibility fallback for already-published media.
  return sources[0];
}

/**
 * Log device information for debugging
 */
export function logDeviceInfo() {
  const capabilities = getDeviceCapabilities();

  console.log('===== Device Detection =====');
  console.log('Platform:', capabilities.platform);
  console.log('Is Smart TV:', capabilities.isSmartTV);
  console.log('Is Legacy:', capabilities.isLegacy);
  console.log('Video Capabilities:', capabilities.video);
  console.log('CSS Support:', capabilities.css);
  console.log('JS Features:', capabilities.js);

  if (capabilities.platform === PLATFORMS.LG_WEBOS) {
    console.log('WebOS Version:', getWebOSVersion());
  }
  if (capabilities.platform === PLATFORMS.SAMSUNG_TIZEN) {
    console.log('Tizen Version:', getTizenVersion());
  }

  console.log('User Agent:', navigator.userAgent);
  console.log('============================');
}

// Export default detection function
const deviceDetection = {
  detectPlatform,
  getDeviceCapabilities,
  getBestVideoSource,
  isSmartTV,
  isLegacyTV,
  getWebOSVersion,
  getTizenVersion,
  logDeviceInfo,
  PLATFORMS
};

export default deviceDetection;
